import { useState, useEffect, useRef } from "react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { STATUS_STEPS, STATUS_META } from "@/data/content";
import { toast } from "sonner";
import {
  Loader2, Users, FileText, CreditCard, MessageSquare, Send, Download, ChevronRight,
} from "lucide-react";

const ALL_STATUSES = ["submitted", "under_review", "processing", "approved", "completed", "rejected"];

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-5 rounded-2xl bg-brand-surface border border-white/10" data-testid={testid}>
      <Icon className="w-5 h-5 text-brand-gold mb-3" />
      <p className="text-3xl font-display">{value}</p>
      <p className="text-white/50 text-sm">{label}</p>
    </div>
  );
}

function UpdateStatusDialog({ app, onUpdated }) {
  const [status, setStatus] = useState(app.status);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await api.patch(`/applications/${app.id}/status`, { status, note });
      toast.success("Status updated.");
      setOpen(false);
      setNote("");
      onUpdated();
    } catch (err) {
      toast.error("Update failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-brand-gold/40 text-brand-gold hover:bg-brand-gold hover:text-brand-navy" data-testid={`update-status-btn-${app.id}`}>
          Update
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-brand-surface border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display">Update Status — {app.title}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="status-select"
            className="w-full h-11 rounded-md bg-brand-navy border border-white/10 text-white px-3">
            {ALL_STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for the client (optional)"
            data-testid="status-note-input" className="bg-brand-navy border-white/10 text-white" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={loading} data-testid="status-save-btn" className="bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminChat({ thread, onSent }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/admin/messages/${thread.user_id}`);
    setMsgs(data);
  };

  useEffect(() => { load(); }, [thread.user_id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setLoading(true);
    try {
      await api.post("/admin/messages", { user_id: thread.user_id, body });
      setBody("");
      await load();
      onSent?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-brand-surface border border-white/10 flex flex-col h-[560px]">
      <div className="p-4 border-b border-white/10">
        <p className="font-display">{thread.user_name}</p>
        <p className="text-white/40 text-xs">{thread.user_email}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${m.sender_role === "admin" ? "bg-brand-gold text-brand-navy rounded-br-sm" : "bg-brand-navy border border-white/10 text-white/90 rounded-bl-sm"}`}>
              {m.body}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply…" data-testid="admin-message-input"
          className="bg-brand-navy border-white/10 text-white" />
        <Button type="submit" disabled={loading} data-testid="admin-message-send-btn" className="bg-brand-gold text-brand-navy hover:bg-brand-goldhover">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [payments, setPayments] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);

  const loadStats = async () => setStats((await api.get("/admin/stats")).data);
  const loadApps = async () => setApps((await api.get("/admin/applications")).data);
  const loadPayments = async () => setPayments((await api.get("/admin/payments")).data);
  const loadThreads = async () => setThreads((await api.get("/admin/threads")).data);

  useEffect(() => { loadStats(); loadApps(); loadPayments(); loadThreads(); }, []);

  const setPayStatus = async (id, status) => {
    try {
      await api.patch(`/payments/${id}/status`, { status });
      toast.success(`Payment ${status}.`);
      loadPayments(); loadStats();
    } catch (err) { toast.error("Update failed."); }
  };

  return (
    <div className="min-h-screen bg-brand-navy text-brand-text">
      <Navbar variant="app" />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 pt-28 pb-20">
        <p className="text-brand-gold text-xs tracking-[0.25em] uppercase mb-2">Admin Console</p>
        <h1 className="font-display text-4xl tracking-tight mb-8">Welcome, {user?.name}</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
            <StatCard icon={FileText} label="Total Applications" value={stats.total_applications} testid="stat-total" />
            <StatCard icon={FileText} label="Active" value={stats.active_applications} testid="stat-active" />
            <StatCard icon={FileText} label="Completed" value={stats.completed_applications} testid="stat-completed" />
            <StatCard icon={CreditCard} label="Pending Payments" value={stats.pending_payments} testid="stat-pending-pay" />
            <StatCard icon={Users} label="Customers" value={stats.customers} testid="stat-customers" />
            <StatCard icon={MessageSquare} label="Unread Msgs" value={stats.unread_messages} testid="stat-unread" />
          </div>
        )}

        <Tabs defaultValue="applications">
          <TabsList className="bg-brand-surface border border-white/10 mb-8">
            <TabsTrigger value="applications" data-testid="admin-tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="applications">
            <div className="rounded-2xl bg-brand-surface border border-white/10 overflow-hidden">
              {apps.length === 0 ? <p className="p-6 text-white/40 text-sm">No applications yet.</p> : (
                <div className="divide-y divide-white/5">
                  {apps.map((a) => (
                    <div key={a.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-app-row-${a.id}`}>
                      <div>
                        <p className="font-medium">{a.title} <span className="text-white/30 text-xs uppercase ml-1">{a.service_type}</span></p>
                        <p className="text-white/40 text-xs">{a.user_name} · {a.user_email} · {a.destination}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={a.status} testid={`admin-app-status-${a.id}`} />
                        <UpdateStatusDialog app={a} onUpdated={() => { loadApps(); loadStats(); }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <div className="rounded-2xl bg-brand-surface border border-white/10 overflow-hidden">
              {payments.length === 0 ? <p className="p-6 text-white/40 text-sm">No payments submitted yet.</p> : (
                <div className="divide-y divide-white/5">
                  {payments.map((p) => (
                    <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-pay-row-${p.id}`}>
                      <div>
                        <p className="font-medium">GHS {p.amount} <span className="text-white/40 text-sm">· {p.method}</span></p>
                        <p className="text-white/40 text-xs">{p.user_name} · Ref: {p.reference || "—"} · {new Date(p.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {p.proof_document_id && (
                          <a href={`${API}/documents/${p.proof_document_id}/download`} target="_blank" rel="noreferrer"
                            className="text-brand-gold text-sm flex items-center gap-1 hover:underline" data-testid={`admin-pay-proof-${p.id}`}>
                            <Download className="w-4 h-4" /> Proof
                          </a>
                        )}
                        <StatusBadge status={p.status} testid={`admin-pay-status-${p.id}`} />
                        {p.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => setPayStatus(p.id, "verified")} data-testid={`verify-pay-${p.id}`}
                              className="bg-emerald-600 hover:bg-emerald-500 text-white">Verify</Button>
                            <Button size="sm" variant="outline" onClick={() => setPayStatus(p.id, "rejected")} data-testid={`reject-pay-${p.id}`}
                              className="border-red-500/40 text-red-300 hover:bg-red-500/10">Reject</Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="rounded-2xl bg-brand-surface border border-white/10 overflow-hidden md:col-span-1 h-[560px] overflow-y-auto">
                <div className="p-4 border-b border-white/10 font-display">Conversations</div>
                {threads.length === 0 ? <p className="p-4 text-white/40 text-sm">No conversations yet.</p> : threads.map((t) => (
                  <button key={t.user_id} onClick={() => setActiveThread(t)} data-testid={`thread-${t.user_id}`}
                    className={`w-full text-left p-4 border-b border-white/5 hover:bg-white/5 flex items-center justify-between ${activeThread?.user_id === t.user_id ? "bg-white/5" : ""}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{t.user_name}</p>
                      <p className="text-white/40 text-xs truncate">{t.last_message}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.unread > 0 && <span className="bg-brand-gold text-brand-navy text-xs px-2 py-0.5 rounded-full font-semibold">{t.unread}</span>}
                      <ChevronRight className="w-4 h-4 text-white/30" />
                    </div>
                  </button>
                ))}
              </div>
              <div className="md:col-span-2">
                {activeThread ? <AdminChat thread={activeThread} onSent={loadThreads} /> : (
                  <div className="rounded-2xl bg-brand-surface border border-white/10 h-[560px] flex items-center justify-center text-white/40">
                    Select a conversation to reply.
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
